import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { ApiClientWrapper } from '@spike/api-client';
import { refreshInboxThunk } from '@spike/omnichannel-action';
import { Inbox } from '@spike/omnichannel-model';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'store';
import ChannelCard from './ChannelCard/ChannelCard';
import EmailSenderModal from './EmailSenderModal';
import { emailChannelCardContent } from './emailChannelCard';
import { inboxRequestErrorMessage } from './inboxRequestError';
import styles from './EmailChannelSection.module.scss';

interface EmailChannelSectionProps {
    inbox?: Inbox;
    apiClientWrapper: ApiClientWrapper;
}

// Same guard as EmailMarketing PendingState: absorb double-taps and give SES a moment to propagate.
const REFRESH_LOCK_SECONDS = 10;

/**
 * Email channel card plus its two actions: the sender modal (Set up / Manage) and, while the own
 * address is being verified, the "I've Confirmed" refresh that re-reads the SES status.
 */
const EmailChannelSection: FunctionComponent<EmailChannelSectionProps> = ({ inbox, apiClientWrapper }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const [modalOpen, setModalOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [locked, setLocked] = useState(false);
    const [refreshError, setRefreshError] = useState<string | null>(null);
    const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
        };
    }, []);

    const card = emailChannelCardContent(inbox);
    const isPending = inbox?.status === 'pending';

    const handleRefresh = async () => {
        if (!inbox || locked || refreshing) return;

        setRefreshing(true);
        setRefreshError(null);
        try {
            await dispatch(refreshInboxThunk(apiClientWrapper, inbox.id));
        } catch (error) {
            setRefreshError(inboxRequestErrorMessage(error, 'Could not check the verification status. Try again.'));
        } finally {
            setRefreshing(false);
            setLocked(true);
            if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
            lockTimerRef.current = setTimeout(() => setLocked(false), REFRESH_LOCK_SECONDS * 1000);
        }
    };

    let refreshLabel = "I've Confirmed";
    if (refreshing) refreshLabel = 'Checking…';
    else if (locked) refreshLabel = 'Checked just now';

    return (
        <div className={styles.root}>
            <ChannelCard
                icon="/images/settings/integrations/email.svg"
                title="Email"
                description={card.description}
                status={card.status}
                buttonLabel={isPending ? 'Edit' : card.buttonLabel}
                buttonDisabled={!inbox}
                onClick={() => setModalOpen(true)}
            />

            {isPending && inbox && (
                <AlertMessage
                    icon={faInfoCircle}
                    iconColor="#BAA997"
                    bdColor="#BCB8AE"
                    bgColor="#F8F5F1"
                    className={styles.alert}
                >
                    <span className={styles.alertCopy}>
                        {inbox.verificationInstructions ??
                            `Check ${inbox.fromIdentity} for the AWS verification email, click the link, then press "I've Confirmed".`}
                    </span>
                    <div className={styles.alertActions}>
                        <Button
                            size="medium"
                            label={refreshLabel}
                            variant="green"
                            onClick={handleRefresh}
                            disabled={refreshing || locked}
                        />
                    </div>
                    {refreshError && <span className={styles.alertError}>{refreshError}</span>}
                </AlertMessage>
            )}

            {modalOpen && inbox && (
                <EmailSenderModal
                    inbox={inbox}
                    apiClientWrapper={apiClientWrapper}
                    onCancel={() => setModalOpen(false)}
                    onSaved={() => setModalOpen(false)}
                />
            )}
        </div>
    );
};

export default EmailChannelSection;
